// features/Home/Home.tsx (or wherever you render Sidebar + Header)
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { Toaster } from "@/components/ui/sonner";
import { SidebarProvider } from "@/components/ui/sidebar";
import { WorkspaceProvider } from "../Workspaces/WorkspaceContext";
import {
  deriveWorkspacesFromWorkbooks,
  deriveWorkspacesFromTables,
  mergeWorkspaceLists,
  normalizeCode,
} from "../Workspaces/deriveWorkspaces";
import { useAuth } from "../Auth";
import Stage from "../Footer/Stage";
import WorkspaceCommandPalette from "../Workspaces/WorkspaceCommandPalette";
import FloatingSidebarTrigger from "./FloatingSidebarTrigger";
import {
  buildWorkspaceAdminPath,
  buildWorkspaceDashboardPath,
  normalizeWorkspaceGroupForPath,
} from "../Workspaces/workspaceRoutes";

export function Home({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const didRedirectToStart = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const wsFromBooks = deriveWorkspacesFromWorkbooks(user);
  const wsFromTables = deriveWorkspacesFromTables(user);
  const available = mergeWorkspaceLists(wsFromBooks, wsFromTables);

  const getDefaultRouteForWorkspace = (code: string | null, group?: string) => {
    const normalized = normalizeCode(code ?? "");
    const requestedGroup = normalizeWorkspaceGroupForPath(group);

    if (user?.workbooks) {
      for (const [groupName, byWorkspace] of Object.entries(user.workbooks)) {
        if (normalizeWorkspaceGroupForPath(groupName) !== requestedGroup)
          continue;
        for (const [workspaceName, byMenu] of Object.entries(byWorkspace)) {
          if (normalizeCode(workspaceName) !== normalized) continue;
          for (const entries of Object.values(byMenu)) {
            for (const uuid of Object.keys(entries)) {
              return buildWorkspaceDashboardPath({
                group: requestedGroup,
                workspace: workspaceName,
                uuid,
              });
            }
          }
        }
      }
    }

    if (user?.tables) {
      for (const [groupName, byMenu] of Object.entries(user.tables)) {
        if (normalizeWorkspaceGroupForPath(groupName) !== requestedGroup)
          continue;
        for (const [sectionName, tables] of Object.entries(byMenu)) {
          if (normalizeCode(sectionName) !== normalized) continue;
          const routeClient =
            user?.section_route_overrides?.[sectionName] ??
            sectionName.toLowerCase();
          const firstTable = Object.keys(tables)[0];
          if (firstTable)
            return buildWorkspaceAdminPath({
              group: requestedGroup,
              workspace: sectionName,
              path: `${routeClient}/${firstTable}`,
            });
        }
      }
    }

    return "/dashboard";
  };

  useEffect(() => {
    if (
      available.length > 1 &&
      !didRedirectToStart.current &&
      location.pathname === "/dashboard"
    ) {
      didRedirectToStart.current = true;
      navigate("/workspace-start", { replace: true });
      return;
    }
  }, [available.length, location.pathname, navigate]);

  return (
    <WorkspaceProvider initialAvailable={available}>
      <SidebarProvider>
        <Sidebar />
        <WorkspaceCommandPalette
          onWorkspaceSelected={(ws, options) => {
            const route = getDefaultRouteForWorkspace(
              ws.code ?? null,
              ws.group,
            );
            if (options?.openInNewTab) {
              window.open(route, "_blank", "noopener,noreferrer");
              return;
            }
            navigate(route);
          }}
        />
        <FloatingSidebarTrigger />
        <div className="flex h-svh min-h-0 flex-1 flex-col overflow-hidden">
          <div className="min-h-0 flex-1 overflow-y-auto" id="main-content">
            {children}
          </div>
          <Stage />
        </div>
        <Toaster />
      </SidebarProvider>
    </WorkspaceProvider>
  );
}
